import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Optional async prefetch to await before fading out (e.g. menusApi.today). */
  prefetch?: () => Promise<unknown>;
  /** Minimum visible duration to avoid flash. */
  minMs?: number;
  onDone?: () => void;
}

/**
 * Branded splash. Sits over the app on first load, fires the optional prefetch,
 * then fades out. Sized so it covers iOS safe areas.
 */
export function SplashScreen({ prefetch, minMs = 1500, onDone }: Props) {
  const [done, setDone] = useState(false);

  // Stocke prefetch / onDone dans des refs pour eviter
  // de relancer l'effet quand App re-render avec de nouvelles
  // closures (les fonctions sont recreees a chaque render).
  const prefetchRef = useRef(prefetch);
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    prefetchRef.current = prefetch;
    onDoneRef.current = onDone;
  }, [prefetch, onDone]);

  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    // Hard timeout : 4s max au cas ou prefetch resterait coince.
    const HARD_TIMEOUT = 4000;
    const hardTimer = setTimeout(() => {
      if (!cancelled) {
        setDone(true);
        onDoneRef.current?.();
      }
    }, HARD_TIMEOUT);

    const finish = () => {
      if (cancelled) return;
      const elapsed = Date.now() - start;
      const wait = Math.max(0, minMs - elapsed);
      setTimeout(() => {
        if (cancelled) return;
        clearTimeout(hardTimer);
        setDone(true);
        onDoneRef.current?.();
      }, wait);
    };

    const p = prefetchRef.current;
    if (p) {
      p()
        .catch(() => {
          /* swallow — splash should never block app boot on network failure */
        })
        .finally(finish);
    } else {
      finish();
    }

    return () => {
      cancelled = true;
      clearTimeout(hardTimer);
    };
    // Mount-only — refs handle changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minMs]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-orange text-white"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1.2] }}
            className="flex h-24 w-24 items-center justify-center rounded-3xl bg-white/95 text-5xl font-black text-orange shadow-[0_20px_60px_rgba(0,0,0,0.2)]"
          >
            M+
          </motion.div>
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="mono mt-6 text-[10px] tracking-[3px]"
          >
            MY FOOD +
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.85 }}
            transition={{ delay: 0.35 }}
            className="font-display mt-2 text-2xl font-extrabold"
          >
            La cantine élue par la communauté
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
